import { useEffect, useState } from "react";

export interface TeamworkMe {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface TeamworkTask {
  id: number;
  content: string;
  description?: string;
  "due-date"?: string;
  priority?: string;
  status?: string;
  "project-name"?: string;
  "tasklist-name"?: string;
  "responsible-party-summary"?: string;
  "company-name"?: string;
}

export interface CalendarEvent {
  id: string;
  summary: string;
  start: string | null;
  end: string | null;
  allDay: boolean;
  htmlLink: string | null;
  location: string | null;
  hangoutLink: string | null;
}

export interface RecentComment {
  id: number;
  description?: string;
  type?: string;
  activitytype?: string;
  "item-link"?: string;
  "item-info"?: string;
  "user-id"?: string;
  "from-name"?: string;
  "project-name"?: string;
  datetime?: string;
}

interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function useFetch<T>(url: string | null): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({
    data: null,
    loading: !!url,
    error: null,
  });

  useEffect(() => {
    if (!url) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    fetch(url)
      .then(async (r) => {
        if (!r.ok) {
          const text = await r.text();
          throw new Error(text || `HTTP ${r.status}`);
        }
        return (await r.json()) as T;
      })
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err: Error) => {
        if (!cancelled)
          setState({ data: null, loading: false, error: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return state;
}

export function useTeamworkMe() {
  return useFetch<TeamworkMe>("/api/teamwork/me");
}

export function useTasksToday(userId: string | null) {
  return useFetch<{ "todo-items"?: TeamworkTask[] }>(
    userId
      ? `/api/teamwork/tasks-today?userId=${encodeURIComponent(userId)}`
      : null,
  );
}

export function useCalendarToday() {
  return useFetch<{ events: CalendarEvent[] }>("/api/calendar/today");
}

export function useRecentTeamworkComments(maxItems = 25) {
  return useFetch<{ activity?: RecentComment[] }>(
    `/api/teamwork/recent-comments?maxItems=${maxItems}`,
  );
}
